import type { CbiFrontendServiceContext } from "../../frontend/context/CbiFrontendServiceContext";
import type { ClassRequirementMode } from "./ClassRequirementMode";
import type { JpClassStaff } from "./JpClassStaff";
import type { JpClassTypeDTOBase } from "./JpClassTypeDTOBase";
import type { JpClassTypeOverrideStaff } from "./JpClassTypeOverrideStaff";
import type { RatingRef } from "./RatingRef";
import type { RatingStaff } from "./RatingStaff";
import type { TrackedBase } from "./TrackedBase";

export class JpClassTypeStaff implements JpClassTypeDTOBase, TrackedBase {
  constructor(public readonly typeId: number, public readonly typeName: string | null, public readonly signupMax: number | null, public readonly allowMultiple: string | null, public readonly ratingPrereqId: RatingStaff | null, public readonly sessionLength: number | null, public readonly sessionCt: number | null, public readonly ratingOverkillId: RatingStaff | null, public readonly minAge: number | null, public readonly maxAge: number | null, public readonly minSessionsForAttended: number | null, public readonly active: string | null, public readonly displayOrder: number | null, public readonly noLimit: string | null, public readonly classPrereqId: JpClassTypeStaff | null, public readonly classOverkillId: JpClassTypeStaff | null, public readonly prereqMustComplete: boolean | null, public readonly allowTeacher: boolean | null, public readonly teachRatingId: RatingStaff | null, public readonly prereqMode: ClassRequirementMode, public readonly overkillMode: ClassRequirementMode, public readonly createdOn: Date | null, public readonly createdBy: string | null, public readonly updatedOn: Date | null, public readonly updatedBy: string | null, public readonly classes: Array<JpClassStaff> | null, public readonly typesRequiringThisAsPrereq: Array<JpClassTypeStaff> | null, public readonly typesRequiringThisAsOverkill: Array<JpClassTypeStaff> | null, public readonly overrides: Array<JpClassTypeOverrideStaff> | null, public readonly ratingPrereqIdTargetIds: RatingRef | null, public readonly ratingOverkillIdTargetIds: RatingRef | null, public readonly teachRatingIdTargetIds: RatingRef | null) {
    this.cbiServiceContext = null;
    this.ratingPrereqIdLoaded = this.ratingPrereqId != null;
    this.ratingPrereqIdCached = this.ratingPrereqId;
    this.ratingOverkillIdLoaded = this.ratingOverkillId != null;
    this.ratingOverkillIdCached = this.ratingOverkillId;
    this.classPrereqIdLoaded = this.classPrereqId != null;
    this.classPrereqIdCached = this.classPrereqId;
    this.classOverkillIdLoaded = this.classOverkillId != null;
    this.classOverkillIdCached = this.classOverkillId;
    this.teachRatingIdLoaded = this.teachRatingId != null;
    this.teachRatingIdCached = this.teachRatingId;
    this.classesLoaded = this.classes != null;
    this.classesCached = this.classes;
    this.typesRequiringThisAsPrereqLoaded = this.typesRequiringThisAsPrereq != null;
    this.typesRequiringThisAsPrereqCached = this.typesRequiringThisAsPrereq;
    this.typesRequiringThisAsOverkillLoaded = this.typesRequiringThisAsOverkill != null;
    this.typesRequiringThisAsOverkillCached = this.typesRequiringThisAsOverkill;
    this.overridesLoaded = this.overrides != null;
    this.overridesCached = this.overrides;
  }
  cbiServiceContext: CbiFrontendServiceContext | null;
  private ratingPrereqIdLoaded: boolean;
  private ratingPrereqIdCached: RatingStaff | null;
  private ratingOverkillIdLoaded: boolean;
  private ratingOverkillIdCached: RatingStaff | null;
  private classPrereqIdLoaded: boolean;
  private classPrereqIdCached: JpClassTypeStaff | null;
  private classOverkillIdLoaded: boolean;
  private classOverkillIdCached: JpClassTypeStaff | null;
  private teachRatingIdLoaded: boolean;
  private teachRatingIdCached: RatingStaff | null;
  private classesLoaded: boolean;
  private classesCached: Array<JpClassStaff> | null;
  private typesRequiringThisAsPrereqLoaded: boolean;
  private typesRequiringThisAsPrereqCached: Array<JpClassTypeStaff> | null;
  private typesRequiringThisAsOverkillLoaded: boolean;
  private typesRequiringThisAsOverkillCached: Array<JpClassTypeStaff> | null;
  private overridesLoaded: boolean;
  private overridesCached: Array<JpClassTypeOverrideStaff> | null;
  async ratingPrereqIdLazy(): Promise<RatingStaff | null> {
    if (!this.ratingPrereqIdLoaded) {
      this.ratingPrereqIdCached = await (this.ratingPrereqIdTargetIds != null ? this.cbiServiceContext!.ratingService().getRatingStaff(this.ratingPrereqIdTargetIds.ratingId) : Promise.resolve(null));
      this.ratingPrereqIdLoaded = true;
    }
    return this.ratingPrereqIdCached;
  }
  async ratingOverkillIdLazy(): Promise<RatingStaff | null> {
    if (!this.ratingOverkillIdLoaded) {
      this.ratingOverkillIdCached = await (this.ratingOverkillIdTargetIds != null ? this.cbiServiceContext!.ratingService().getRatingStaff(this.ratingOverkillIdTargetIds.ratingId) : Promise.resolve(null));
      this.ratingOverkillIdLoaded = true;
    }
    return this.ratingOverkillIdCached;
  }
  async classPrereqIdLazy(): Promise<JpClassTypeStaff | null> {
    if (!this.classPrereqIdLoaded) {
      this.classPrereqIdCached = await this.cbiServiceContext!.jpClassService().getJpClassTypeStaffClassPrereqId(this.typeId);
      this.classPrereqIdLoaded = true;
    }
    return this.classPrereqIdCached;
  }
  async classOverkillIdLazy(): Promise<JpClassTypeStaff | null> {
    if (!this.classOverkillIdLoaded) {
      this.classOverkillIdCached = await this.cbiServiceContext!.jpClassService().getJpClassTypeStaffClassOverkillId(this.typeId);
      this.classOverkillIdLoaded = true;
    }
    return this.classOverkillIdCached;
  }
  async teachRatingIdLazy(): Promise<RatingStaff | null> {
    if (!this.teachRatingIdLoaded) {
      this.teachRatingIdCached = await (this.teachRatingIdTargetIds != null ? this.cbiServiceContext!.ratingService().getRatingStaff(this.teachRatingIdTargetIds.ratingId) : Promise.resolve(null));
      this.teachRatingIdLoaded = true;
    }
    return this.teachRatingIdCached;
  }
  async classesLazy(): Promise<Array<JpClassStaff>> {
    if (!this.classesLoaded) {
      this.classesCached = await this.cbiServiceContext!.jpClassService().getJpClassTypeStaffClasses(this.typeId);
      this.classesLoaded = true;
    }
    return this.classesCached!;
  }
  async typesRequiringThisAsPrereqLazy(): Promise<Array<JpClassTypeStaff>> {
    if (!this.typesRequiringThisAsPrereqLoaded) {
      this.typesRequiringThisAsPrereqCached = await this.cbiServiceContext!.jpClassService().getJpClassTypeStaffTypesRequiringThisAsPrereq(this.typeId);
      this.typesRequiringThisAsPrereqLoaded = true;
    }
    return this.typesRequiringThisAsPrereqCached!;
  }
  async typesRequiringThisAsOverkillLazy(): Promise<Array<JpClassTypeStaff>> {
    if (!this.typesRequiringThisAsOverkillLoaded) {
      this.typesRequiringThisAsOverkillCached = await this.cbiServiceContext!.jpClassService().getJpClassTypeStaffTypesRequiringThisAsOverkill(this.typeId);
      this.typesRequiringThisAsOverkillLoaded = true;
    }
    return this.typesRequiringThisAsOverkillCached!;
  }
  async overridesLazy(): Promise<Array<JpClassTypeOverrideStaff>> {
    if (!this.overridesLoaded) {
      this.overridesCached = await this.cbiServiceContext!.jpClassService().getJpClassTypeStaffOverrides(this.typeId);
      this.overridesLoaded = true;
    }
    return this.overridesCached!;
  }
}
